import { useEffect, useRef, useState } from "react";
import { MapPin, BadgeCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";

let enabledPromise = null;
const placesEnabled = () => (enabledPromise ||= api.get("/places/status").then((r) => !!r.data.enabled).catch(() => false));
const newSession = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

/**
 * Address input with Google Places suggestions (proxied through /api/places). Plain input when not configured.
 * onChange(address, verified) — verified=true only when picked from Google.
 */
export function AddressInput({ value, verified = false, onChange, className, placeholder = "Street, city, state ZIP", testid = "address-input", disabled = false, required = false }) {
  const [enabled, setEnabled] = useState(false);
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(-1);
  const session = useRef(null);
  const timer = useRef(null);
  const box = useRef(null);
  const seq = useRef(0);

  useEffect(() => { placesEnabled().then(setEnabled); }, []);
  useEffect(() => {
    const close = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const search = (q) => {
    clearTimeout(timer.current);
    if (!enabled || q.trim().length < 4) { setSugs([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      const my = ++seq.current;
      try {
        setBusy(true);
        session.current = session.current || newSession();
        const { data } = await api.post("/places/autocomplete", { input: q, session: session.current, language: navigator.language || "en" });
        if (my !== seq.current) return;
        setSugs(data); setOpen(data.length > 0); setActive(-1);
      } catch { setSugs([]); }
      finally { if (my === seq.current) setBusy(false); }
    }, 250);
  };

  const pick = async (s) => {
    setOpen(false);
    try {
      setBusy(true);
      const { data } = await api.get(`/places/${s.place_id}`, { params: { session: session.current || "" } });
      onChange(data.formatted_address || s.text, true);
    } catch { onChange(s.text, true); }
    finally { session.current = null; setBusy(false); }
  };

  const onKey = (e) => {
    if (!open || sugs.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, sugs.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(sugs[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={box} className="relative">
      <input value={value} disabled={disabled} required={required} placeholder={placeholder} autoComplete="off" data-testid={testid} className={`${className} ${enabled ? "pr-24" : ""}`}
        onChange={(e) => { onChange(e.target.value, false); search(e.target.value); }} onFocus={() => sugs.length && setOpen(true)} onKeyDown={onKey} />
      {enabled && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[10px] font-semibold pointer-events-none" data-testid={`${testid}-status`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" /> : verified && value
            ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5"><BadgeCheck className="h-3 w-3" /> Verified</span>
            : <span className="inline-flex items-center gap-1 text-slate-400"><MapPin className="h-3 w-3" /> Google</span>}
        </span>
      )}
      {open && sugs.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-suggestions`} role="listbox">
          {sugs.map((s, i) => (
            <li key={s.place_id} role="option" aria-selected={i === active} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)} data-testid={`${testid}-suggestion-${i}`}
              className={`flex items-start gap-2 px-3 py-2 text-sm cursor-pointer ${i === active ? "bg-primary/10" : "hover:bg-slate-50"}`}>
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
              <span className="min-w-0"><span className="block font-medium text-slate-900 truncate">{s.main}</span>{s.secondary && <span className="block text-xs text-slate-500 truncate">{s.secondary}</span>}</span>
            </li>
          ))}
          <li className="px-3 py-1 text-[10px] text-slate-400 text-right border-t border-slate-100">Suggestions by Google</li>
        </ul>
      )}
    </div>
  );
}
